/**
 * Los nombres en los archivos de constantes se escriben con el título
 * académico en español antes del nombre (p. ej. "Dr. Renán Quijano",
 * "Mtra. Andrea Garma", "Ing. Marco González").
 *
 * Cada idioma tiene su propia convención para esos títulos:
 *
 *  - ES: se conservan todos los prefijos tal cual.
 *  - EN: "Dr." es válido como prefijo (también para doctorado académico);
 *        "Mtro./Mtra." e "Ing./Lic." no tienen prefijo estándar, se omiten.
 *  - FR: "Dr" antes del nombre se reserva a profesiones de salud; el
 *        doctorado académico se indica después del nombre ("Nombre, PhD").
 *        Maestría e ingeniería no tienen prefijo estándar: solo el nombre.
 */

type TitleGroup = "doctor" | "master" | "engineer";

const SPANISH_TITLES: Record<string, TitleGroup> = {
  "dr.": "doctor",
  "dra.": "doctor",
  "mtro.": "master",
  "mtra.": "master",
  "ing.": "engineer",
  "lic.": "engineer",
};

type TitleRule =
  | { kind: "keep" } // deja el nombre con su prefijo en español
  | { kind: "none" } // solo el nombre, sin título
  | { kind: "prefix"; text: string }
  | { kind: "suffix"; text: string };

const RULES_BY_LANGUAGE: Record<string, Record<TitleGroup, TitleRule>> = {
  ES: { doctor: { kind: "keep" }, master: { kind: "keep" }, engineer: { kind: "keep" } },
  EN: { doctor: { kind: "prefix", text: "Dr." }, master: { kind: "none" }, engineer: { kind: "none" } },
  FR: { doctor: { kind: "suffix", text: "PhD" }, master: { kind: "none" }, engineer: { kind: "none" } },
};

export function localizeName(name: string, language: string): string {
  const spaceIdx = name.indexOf(" ");
  if (spaceIdx === -1) return name;

  const first = name.slice(0, spaceIdx);
  const rest = name.slice(spaceIdx + 1);
  const group = SPANISH_TITLES[first.toLowerCase()];
  if (!group) return name;

  const rule = RULES_BY_LANGUAGE[language.toUpperCase()]?.[group] ?? { kind: "keep" };
  switch (rule.kind) {
    case "keep":
      return name;
    case "none":
      return rest;
    case "prefix":
      return `${rule.text} ${rest}`;
    case "suffix":
      return `${rest}, ${rule.text}`;
  }
}
